import { PDFDocument, PDFImage, PDFPage, StandardFonts, rgb } from "pdf-lib";
import { PDF_FOOTER_TEXT, getPdfHeaderLines, type PdfHeaderData } from "./pdfHeader";

const fetchLogoBytes = async (url: string): Promise<{ bytes: Uint8Array; isPng: boolean } | null> => {
  try {
    const resp = await fetch(url);
    if (!resp.ok) return null;
    const blob = await resp.blob();
    const bytes = new Uint8Array(await blob.arrayBuffer());
    const type = (blob.type || "").toLowerCase();
    const isPng = type.includes("png");
    return { bytes, isPng };
  } catch {
    return null;
  }
};

export async function applyPdfHeaderFooterToBlob(
  input: Blob,
  headerData?: PdfHeaderData | null,
  opts?: { includeHeader?: boolean; includeFooter?: boolean; contentOffsetY?: number }
): Promise<Blob> {
  try {
    const data = new Uint8Array(await input.arrayBuffer());
    const doc = await PDFDocument.load(data);
    const font = await doc.embedFont(StandardFonts.Helvetica);
    const pages = doc.getPages();
    const lines = getPdfHeaderLines(headerData);
    const headerFontSize = 8;
    const footerSize = 9;
    const lineHeight = headerFontSize + 3;
    const includeHeader = opts?.includeHeader !== false;
    const includeFooter = opts?.includeFooter !== false;
    const headerHeight = lines.length
      ? (lines.length - 1) * lineHeight + headerFontSize
      : 0;
    const contentOffsetY = opts?.contentOffsetY ?? (includeHeader ? headerHeight + 10 : 0);
    const textColor = rgb(0.1, 0.12, 0.16);
    const footerColor = rgb(0.4, 0.45, 0.5);

    let logoImage: PDFImage | null = null;
    if (headerData?.logoUrl) {
      const logo = await fetchLogoBytes(headerData.logoUrl);
      if (logo) {
        try {
          logoImage = logo.isPng ? await doc.embedPng(logo.bytes) : await doc.embedJpg(logo.bytes);
        } catch {
          logoImage = null;
        }
      }
    }

    pages.forEach((page: PDFPage) => {
      if (contentOffsetY) page.translateContent(0, -contentOffsetY);
      const { width, height } = page.getSize();
      const headerX = 40;
      const headerTop = height + 20;

      if (includeHeader) {
        lines.forEach((line, idx) => {
          page.drawText(line, {
            x: headerX,
            y: headerTop - idx * lineHeight,
            size: headerFontSize,
            font,
            color: textColor,
          });
        });

        if (logoImage) {
          const maxW = 140;
          const maxH = 48;
          const scale = Math.min(maxW / logoImage.width, maxH / logoImage.height, 1);
          const drawW = logoImage.width * scale;
          const drawH = logoImage.height * scale;
          const x = width - 40 - drawW;
          const y = headerTop + headerFontSize - drawH;
          page.drawImage(logoImage, { x, y, width: drawW, height: drawH });
        }
      }

      if (includeFooter) {
        const footerWidth = font.widthOfTextAtSize(PDF_FOOTER_TEXT, footerSize);
        page.drawText(PDF_FOOTER_TEXT, {
          x: (width - footerWidth) / 2,
          y: 18,
          size: footerSize,
          font,
          color: footerColor,
        });
      }
    });

    const out = await doc.save();
    const outBytes = new Uint8Array(out);
    return new Blob([outBytes.buffer], { type: "application/pdf" });
  } catch {
    return input;
  }
}
